import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, inject, OnInit, ChangeDetectionStrategy, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DEMO_PILLS } from '../demo-responses';
import type { ChatResponse, DemoPill, PromptSuggestion } from '../colimind-types';
import type { DefaultSuggestion } from '../default-suggestions';
import { NotificationService } from '../../../services/notification/notification.service';
import { ColimindDebugService } from '../../../services/colimind/colimind-debug.service';

// Number of greetingTitle<n><Period> wordings per daytime period in the i18n files.
const GREETING_TITLE_VARIANTS = 3;

@Component({
  selector: 'colimo-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './colimind-dashboard.component.html',
  styleUrls: ['./colimind-dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  @Input() userName = '';
  @Input() suggestions: PromptSuggestion[] = [];
  @Input() quickActions: DefaultSuggestion[] = [];
  /** Pulses the input once, right after the onboarding, to show where to start. */
  @Input() highlightInput = false;
  /** Shown after a chat link that could not be opened. */
  @Input() chatNotFound = false;
  @Output() send = new EventEmitter<string>();

  @Output() demo = new EventEmitter<{
    question: string;
    response: ChatResponse;
  }>();

  @ViewChild('inputEl') private inputEl?: ElementRef<HTMLTextAreaElement>;

  /** Lets the page put the caret here, e.g. as the cue right after the onboarding. */
  focusInput(): void {
    this.inputEl?.nativeElement.focus();
  }

  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private notifications = inject(NotificationService);
  readonly debug = inject(ColimindDebugService);

  // Hidden, also in debug mode, until the schedules are implemented
  readonly alertBannerAvailable = false;
  readonly greetingText = signal('');
  readonly greetingTitle = signal('');

  /**
   * NEW alerts, read from the count the sidenav already polls -- no request of our own.
   * The endpoint reports `newRed` / `newYellow`, i.e. alerts the user has not seen yet, not
   * the total of unresolved ones. The wording says "new" so it matches the data.
   */
  private newAlerts = 0;

  /**
   * Drawn once per mount, so a language switch re-translates without re-rolling the wording.
   * Kept as a fraction rather than an index: the headline pool grows by one entry when there
   * are new alerts, and scaling the fraction to the current pool size keeps every candidate
   * equally likely in both cases. The alert line therefore takes its turn like any other
   * variant instead of permanently crowding the questions out.
   */
  private readonly titleRoll = Math.random();

  input = '';
  demoPills: DemoPill[] = DEMO_PILLS;
  private readonly alertQuestionKey = 'colimind.alertQuestion';

  ngOnInit(): void {
    this.updateGreeting();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateGreeting());

    // Read-only: getAlertCountObservable() replays the value the sidenav's polling already
    // produced. Never trigger a fetch here -- a 401/403 on page load would bounce us to login.
    this.notifications.getAlertCountObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(count => {
        this.newAlerts = (count?.newRed ?? 0) + (count?.newYellow ?? 0);
        this.updateGreeting();
      });
  }

  private updateGreeting(): void {
    const period = this.currentPeriod();
    const textKey = `colimind.greeting${period}`;

    const titleKey = this.pickTitleKey(period);

    // get() resolves once loaded; instant() would render the raw key before then.
    // Only `count` is interpolated: the name is rendered next to the greeting in the template.
    this.translate.get([textKey, titleKey], { count: this.newAlerts })
      .subscribe((t: Record<string, string>) => {
        this.greetingText.set(t[textKey]);
        this.greetingTitle.set(t[titleKey]);
      });
  }

  /**
   * One headline out of the pool for this mount. Without new alerts the pool holds the three
   * time based questions; with alerts it holds a fourth entry, so the alert line shows up in
   * roughly one of four visits instead of every single one.
   */
  private pickTitleKey(period: string): string {
    const questions = Array.from(
      { length: GREETING_TITLE_VARIANTS },
      (_, i) => `colimind.greetingTitle${i + 1}${period}`,
    );
    if (this.newAlerts === 0) {
      return this.pick(questions);
    }
    // The name already appears in the greeting line above, so the alert wording leaves it out.
    const alertKey = this.newAlerts === 1 ? 'colimind.greetingAlert' : 'colimind.greetingAlerts';
    const pool = [...questions, alertKey];
    return this.pick(pool);
  }

  private pick(pool: string[]): string {
    return pool[Math.min(Math.floor(this.titleRoll * pool.length), pool.length - 1)];
  }

  private currentPeriod(): string {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 12) return 'Morning';
    if (hour >= 12 && hour < 18) return 'Afternoon';
    if (hour >= 18 && hour < 22) return 'Evening';
    return 'Night';
  }

  /**
   * The tiles and the alert banner are prompts, not labels: they are sent to the assistant as
   * the user's question. Resolving them here rather than hardcoding English means a German
   * user's click also asks in German, which is what the language profile promises.
   */
  onQuickAction(action: DefaultSuggestion): void {
    this.send.emit(this.translate.instant(action.questionKey));
  }

  onAlertBanner(): void {
    this.send.emit(this.translate.instant(this.alertQuestionKey));
  }

  onDemoPill(pill: DemoPill): void {
    this.demo.emit({ question: pill.question, response: pill.response });
  }

  submit(): void {
    const q = this.input.trim();
    if (!q) return;
    this.input = '';
    this.send.emit(q);
  }

  autoResize(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }
}
